import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, ROBYN_PHOTO } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Radio,
  Video,
  FileCheck,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  Clock,
  ExternalLink,
  Play,
  RefreshCw,
  Youtube,
  Facebook,
  Instagram,
  Tv
} from 'lucide-react';

const EngageHubPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [featuredStream, setFeaturedStream] = useState(null);
  const [upcomingStreams, setUpcomingStreams] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStreams = async () => {
    try {
      const [featuredRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/streams/featured`),
        axios.get(`${API}/streams/upcoming`)
      ]);
      setFeaturedStream(featuredRes.data);
      setUpcomingStreams(upcomingRes.data);
    } catch (error) {
      console.error('Failed to fetch streams:', error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch social links:', error);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchStreams();
    fetchSocialLinks();
  }, []);

  const handleFeedback = async (id, helpful) => {
    try {
      await axios.post(`${API}/announcements/${id}/feedback`, null, { params: { helpful } });
      toast.success('Thank you for your feedback!');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const filteredAnnouncements = activeTab === 'all'
    ? announcements
    : announcements.filter(a => a.category === activeTab);

  const categoryLabels = {
    truth_tracker: 'Truth Tracker',
    update: 'Update',
    event: 'Event'
  };

  const categoryColors = {
    truth_tracker: 'bg-emerald-500',
    update: 'bg-blue-500',
    event: 'bg-purple-500'
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'youtube': return <Youtube className="w-5 h-5" />;
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'instagram': return <Instagram className="w-5 h-5" />;
      default: return <Tv className="w-5 h-5" />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'youtube': return 'bg-red-600';
      case 'facebook': return 'bg-blue-600';
      case 'instagram': return 'bg-gradient-to-r from-purple-500 to-pink-500';
      default: return 'bg-plp-blue';
    }
  };

  // Convert platform stream URL to embed URL
  const getEmbedUrl = (stream) => {
    if (!stream) return null;
    
    const url = stream.stream_url;
    
    // YouTube
    if (stream.platform === 'youtube') {
      // Handle various YouTube URL formats
      const videoIdMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (videoIdMatch) {
        return `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1`;
      }
      // If already an embed URL
      if (url.includes('embed')) return url;
    }
    
    // Facebook
    if (stream.platform === 'facebook') {
      // Facebook embed requires special handling
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=560`;
    }
    
    // Instagram - Instagram doesn't support embedding live videos directly
    // For Instagram, we'll show a link instead
    
    return url;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-3">
            <Radio className="w-10 h-10 text-emerald-500" />
            Robyn Responds
          </h1>
          <p className="mt-2 text-slate-600">
            Stay connected with live updates, fact-checked news, and direct engagement with Robyn Lyons.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Stream Section */}
            <Card className="bg-white overflow-hidden" data-testid="live-stream-card">
              <CardHeader className={`text-white ${featuredStream?.is_live ? 'bg-red-600' : 'bg-plp-blue'}`}>
                <CardTitle className="flex items-center justify-between font-heading">
                  <div className="flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    {featuredStream?.is_live ? 'LIVE NOW' : 'Live Engagement'}
                  </div>
                  {featuredStream?.is_live && (
                    <Badge className="bg-white text-red-600 animate-pulse">
                      <span className="w-2 h-2 rounded-full bg-red-600 mr-2 inline-block"></span>
                      LIVE
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {featuredStream ? (
                  <div>
                    <div className="aspect-video bg-slate-900">
                      {featuredStream.platform === 'instagram' ? (
                        // Instagram doesn't support live embeds, show a link
                        <div className="h-full flex flex-col items-center justify-center text-white p-8 text-center">
                          <Instagram className="w-16 h-16 mb-4 text-pink-500" />
                          <p className="font-heading font-semibold text-xl mb-2">{featuredStream.title}</p>
                          <p className="text-white/70 mb-4">{featuredStream.description}</p>
                          <a 
                            href={featuredStream.stream_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Watch on Instagram
                          </a>
                        </div>
                      ) : (
                        <iframe
                          src={getEmbedUrl(featuredStream)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={featuredStream.title}
                        />
                      )}
                    </div>
                    <div className="p-4 bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${getPlatformColor(featuredStream.platform)} text-white`}>
                          {getPlatformIcon(featuredStream.platform)}
                          <span className="ml-1">{featuredStream.platform}</span>
                        </Badge>
                        {featuredStream.scheduled_date && !featuredStream.is_live && (
                          <span className="text-sm text-slate-500">
                            {featuredStream.scheduled_date} at {featuredStream.scheduled_time}
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading font-bold text-lg">{featuredStream.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{featuredStream.description}</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="aspect-video bg-slate-900 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Play className="w-10 h-10 text-white" />
                        </div>
                        <p className="text-white font-heading font-semibold">No Live Stream Right Now</p>
                        <p className="text-white/70 text-sm mt-2">Check back for scheduled Porch Chats</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50">
                      <p className="text-sm text-slate-600">
                        Join Robyn's weekly "Porch Chats" for live Q&A sessions. Ask questions, share concerns, 
                        and be part of the conversation.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Truth Tracker / Announcements */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-emerald-500" />
                  Truth Tracker & Updates
                </h2>
                <Button variant="ghost" size="sm" onClick={fetchAnnouncements} data-testid="refresh-announcements">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="bg-white border">
                  <TabsTrigger value="all" data-testid="tab-all-announcements">All</TabsTrigger>
                  <TabsTrigger value="truth_tracker" data-testid="tab-truth-tracker">Truth Tracker</TabsTrigger>
                  <TabsTrigger value="update" data-testid="tab-updates">Updates</TabsTrigger>
                  <TabsTrigger value="event" data-testid="tab-events">Events</TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab}>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-plp-blue animate-spin" />
                    </div>
                  ) : filteredAnnouncements.length === 0 ? (
                    <Card className="bg-white">
                      <CardContent className="py-12 text-center">
                        <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No announcements yet</p>
                        <p className="text-sm text-slate-400 mt-2">Check back soon for updates from Robyn</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {filteredAnnouncements.map((item) => (
                        <Card key={item.id} className="bg-white" data-testid={`announcement-${item.id}`}>
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <Badge className={`${categoryColors[item.category]} text-white mb-3`}>
                                  {categoryLabels[item.category]}
                                </Badge>
                                <h3 className="font-heading font-bold text-lg text-slate-900">{item.title}</h3>
                                <p className="mt-2 text-slate-600">{item.content}</p>
                                <p className="mt-3 text-xs text-slate-400">
                                  {new Date(item.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                            </div>
                            
                            {/* Feedback */}
                            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                              <span className="text-sm text-slate-500">Was this helpful?</span>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleFeedback(item.id, true)}
                                  className="text-slate-500 hover:text-emerald-600"
                                  data-testid={`feedback-yes-${item.id}`}
                                >
                                  <ThumbsUp className="w-4 h-4 mr-1" />
                                  Yes ({item.helpful_yes})
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleFeedback(item.id, false)}
                                  className="text-slate-500 hover:text-red-600"
                                  data-testid={`feedback-no-${item.id}`}
                                >
                                  <ThumbsDown className="w-4 h-4 mr-1" />
                                  No ({item.helpful_no})
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Streams */}
            <Card className="bg-white" data-testid="upcoming-streams">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-plp-blue" />
                  Upcoming Streams
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {upcomingStreams.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No upcoming streams scheduled</p>
                ) : (
                  upcomingStreams.map((stream) => (
                    <div key={stream.id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        {getPlatformIcon(stream.platform)}
                        <Badge variant="outline" className="text-xs">{stream.platform}</Badge>
                      </div>
                      <h4 className="font-heading font-semibold text-slate-900">{stream.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{stream.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                        {stream.scheduled_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {stream.scheduled_date}
                          </span>
                        )}
                        {stream.scheduled_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {stream.scheduled_time}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* AI Town Hall Info */}
            <Card className="bg-gradient-to-br from-plp-blue to-plp-blue-light text-white overflow-hidden">
              <CardContent className="p-0">
                <img src={ROBYN_PHOTO} alt="Robyn Lyons" className="w-full h-40 object-cover object-top" />
                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg mb-2">Ask Robyn - AI Town Hall</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Can't make a live stream? Use our AI-powered "Ask Robyn" chatbot available 24/7 
                    at the bottom right of your screen!
                  </p>
                  <p className="text-xs text-white/60">
                    The chatbot can answer questions about Robyn's platform, PLP policies, and community resources.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Follow Robyn</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {socialLinks.length === 0 ? (
                  // Default social links if none configured
                  [
                    { platform: 'facebook', url: '#', display_name: 'Facebook' },
                    { platform: 'instagram', url: '#', display_name: 'Instagram' },
                    { platform: 'youtube', url: '#', display_name: 'YouTube' }
                  ].map((social) => (
                    <a
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid={`social-${social.platform}`}
                    >
                      <div className="flex items-center gap-3">
                        {getPlatformIcon(social.platform)}
                        <span className="font-medium text-slate-700">{social.display_name}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </a>
                  ))
                ) : (
                  socialLinks.map((social) => (
                    <a
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid={`social-${social.platform}`}
                    >
                      <div className="flex items-center gap-3">
                        {getPlatformIcon(social.platform)}
                        <span className="font-medium text-slate-700">{social.display_name || social.platform}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </a>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngageHubPage;
